import type { LadderTemplate, PlayerStanding } from '../types';

export interface LadderSlotAssignment {
  matchId: string;
  position: 'player1' | 'player2';
  playerId: string;
}

export function mapStandingsToLadderSlots(
  standings: PlayerStanding[],
  template: LadderTemplate,
): LadderSlotAssignment[] {
  if (standings.length < template.requiredStandings) {
    throw new Error(
      `Standings has ${standings.length} positions but template required ${template.requiredStandings}`,
    );
  }

  const assignments: LadderSlotAssignment[] = [];

  for (const match of template.matches) {
    for (const slotName of ['player1', 'player2'] as const) {
      const slot = match[slotName];
      if (slot.from === 'standings' && slot.position) {
        const standing = standings.find((s) => s.position === slot.position);
        if (!standing) {
          throw new Error(`No player at standings position ${slot.position}`);
        }
        assignments.push({
          matchId: match.id,
          position: slotName,
          playerId: standing.playerId,
        });
      }
    }
  }

  return assignments;
}

/**
 * Resolve a multi-pool ladder template (e.g. Pool Play-offs) against per-pool standings.
 * Each `{ from: 'standings', pool, position }` slot is filled from that pool's standings.
 * Supports PARTIAL advancement: pass only the pools that are complete and the slots for
 * the missing pools are left empty (their side fills when that pool finishes).
 * `bye` slots (orphan in odd-count draws) produce no assignment.
 */
export function mapPoolStandingsToLadderSlots(
  standingsByPool: Map<'A' | 'B', PlayerStanding[]>,
  template: LadderTemplate,
): LadderSlotAssignment[] {
  const assignments: LadderSlotAssignment[] = [];

  for (const match of template.matches) {
    for (const slotName of ['player1', 'player2'] as const) {
      const slot = match[slotName];
      if (slot.from !== 'standings' || !slot.pool || !slot.position) continue;

      const poolStandings = standingsByPool.get(slot.pool);
      if (!poolStandings) {
        // Pool not complete yet — leave this slot empty; it fills when that pool finishes.
        continue;
      }
      const standing = poolStandings.find((s) => s.position === slot.position);
      if (!standing) {
        throw new Error(`No player at pool ${slot.pool} position ${slot.position}`);
      }
      assignments.push({ matchId: match.id, position: slotName, playerId: standing.playerId });
    }
  }

  return assignments;
}
